import React, { Component } from 'react'
import accounting from 'accounting'
import uniqueId from 'lodash/uniqueId'
import get from 'lodash/get'
import has from 'lodash/has'
import classnames from 'classnames'

import { Select } from '../../../'
import Graph from './Graph'
import Tile from './Tile'

import { convertPeriodToDates } from '../../../../utils/dates'

import { PerformanceStore } from '../../../../typings/stores/PerformanceStore'
import { DateRange } from '../../../../typings/dates'

import styles from './styles.module.css'

interface PerformanceOverviewProps {
  performance?: PerformanceStore
  selectedDateRange?: DateRange
  handleFilterChange(dateRange?: DateRange): void
}

const tiles = [
  {
    title: 'Clicks',
    path: 'totals.clicks',
    formatter: value => accounting.formatNumber(value),
  },
  {
    title: 'EPC',
    path: 'totals.epc',
    formatter: value => accounting.formatMoney(value),
  },
  {
    title: 'Sales',
    path: 'totals.sales',
    formatter: value => accounting.formatNumber(value),
  },
  {
    title: 'Conversion Rate',
    path: 'totals.conversionRate',
    formatter: value => `${accounting.toFixed(value, 2)}%`,
  },
  {
    title: 'Commission',
    path: 'totals.commissions',
    formatter: value => accounting.formatMoney(value),
  },
]

export default class PerformanceOverview extends Component<
  PerformanceOverviewProps
> {
  static defaultProps = {
    selectedDateRange: {
      period: 'last_week',
    },
  }

  state = {
    dateType: 'processDate',
    displayType: 'clicks',
    dateRange: {
      period: this.props.selectedDateRange.period,
    },
  }

  componentDidMount = () => {
    const dateRange = convertPeriodToDates(this.props.selectedDateRange.period)

    this.setState({
      dateRange,
    })
  }

  componentDidUpdate = (prevProps, prevState) => {
    const { handleFilterChange } = this.props
    const { dateRange } = this.state

    if (prevState.dateRange.period !== dateRange.period) {
      handleFilterChange(dateRange)
    }
  }

  handlePeriodChange = period => {
    const dateRange = convertPeriodToDates(period)

    this.setState({
      dateRange,
    })
  }

  handleDateTypeChange = dateType => {
    this.setState({
      dateType,
    })
  }

  handleDisplayTypeChange = displayType => {
    this.setState({
      displayType,
    })
  }

  render = () => {
    const { performance } = this.props
    const { dateRange, dateType, displayType } = this.state

    const graphData = get(performance, `data.${dateType}.transactions`)

    return (
      <div className={styles.container} data-rv="performance-overview-widget">
        <div className={styles.header} data-rv="po-header">
          <div className={styles.titleBar}>
            <h2 data-rv="po-title">Performance Overview</h2>
            <span className={styles.titleNote} data-rv="po-title-note">
              Note: Data is delayed by one day
            </span>
          </div>
          <div
            className={styles.dateTypeSelector}
            data-rv="po-datetype-selector"
          >
            <h4>Date Type:</h4>
            <div className={styles.buttonContainer}>
              <button
                className={classnames(styles.toggleButton, {
                  [styles.selected]: dateType === 'clickDate',
                })}
                onClick={() => this.handleDateTypeChange('clickDate')}
              >
                Click Date
              </button>
              <button
                className={classnames(styles.toggleButton, {
                  [styles.selected]: dateType === 'processDate',
                })}
                onClick={() => this.handleDateTypeChange('processDate')}
              >
                Process Date
              </button>
            </div>
          </div>
          <div className={styles.rangeSelector} data-rv="po-range-selector">
            <Select
              selected={dateRange.period}
              onChange={e => this.handlePeriodChange(e.currentTarget.value)}
              options={[
                { text: 'This Week', value: 'this_week' },
                { text: 'Last Week', value: 'last_week' },
                { text: 'This Month', value: 'this_month' },
                { text: 'Last Month', value: 'last_month' },
              ]}
            />
          </div>
        </div>
        <div className={styles.tileContainer} data-rv="po-tile-container">
          {tiles.map(({ title, path, formatter }) => {
            const valuePath = `data.${dateType}.${path}`
            const displayValue = has(performance, valuePath)
              ? get(performance, valuePath)
              : null

            const formattedValue =
              typeof formatter !== 'undefined'
                ? formatter(displayValue)
                : displayValue

            return (
              <Tile
                title={title}
                value={displayValue ? formattedValue : displayValue}
                loading={
                  has(performance, 'fetching') ? performance.fetching : false
                }
                key={uniqueId('tile-')}
              />
            )
          })}
        </div>
        <div className={styles.graphContainer}>
          <div
            className={styles.displayTypeSelector}
            data-rv="po-displaytype-selector"
          >
            <h4>Display Type:</h4>
            <div className={styles.buttonContainer}>
              <button
                className={classnames(styles.toggleButton, {
                  [styles.selected]: displayType === 'clicks',
                })}
                onClick={() => this.handleDisplayTypeChange('clicks')}
              >
                Clicks
              </button>
              <button
                className={classnames(styles.toggleButton, {
                  [styles.selected]: displayType === 'sales',
                })}
                onClick={() => this.handleDisplayTypeChange('sales')}
              >
                Sales
              </button>
              <button
                className={classnames(styles.toggleButton, {
                  [styles.selected]: displayType === 'commissions',
                })}
                onClick={() => this.handleDisplayTypeChange('commissions')}
              >
                Commissions
              </button>
            </div>
          </div>
          <Graph
            dateRange={dateRange}
            graphData={graphData}
            displayType={displayType}
            isLoading={performance.fetching}
          />
        </div>
      </div>
    )
  }
}
